import { useOutletContext, useNavigate } from "react-router-dom";
import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faLink, faGear, faUsersGear, faCode, faSliders } from '@fortawesome/free-solid-svg-icons'
import { useAuth0 } from "@auth0/auth0-react";
import { apiGet } from "../services/AuthService.js";

const Settings = props => {

    const baseUrl = process.env.REACT_APP_API_BASEURL;
    const { getAccessTokenSilently } = useAuth0();
    const [currentAccount, accountProfile, setAccountProfile, toggleIsShowSidebarMd, setIsShowSidebarMd] = useOutletContext();
    const navigate = useNavigate();

    // Doc-conversion state for the IMPERSONATED account (not the logged-in user's
    // role). Two pieces are required to detect "CDEX doc conversion" the same way
    // the admin dashboard does (see DOC_CONV_MAP in AdminDashboard.js, entry "4":
    //   { network: 2, output: 2 }   // TOTDATA - Cdex
    // ). Both must equal 2 for the cards to show.
    //   - networkType === 2 → account uses the TOTData doc-conv network
    //   - outputType === 2  → that conversion is configured to produce CDEX output
    // Both default to null while the fetches are in flight so the cards don't briefly
    // flash before being hidden again.
    const [accountDocConversionNetworkType, setAccountDocConversionNetworkType] = useState(null);
    const [accountDocConversionOutputType, setAccountDocConversionOutputType] = useState(null);

    useEffect(() => {
        (async () => {
            if (setIsShowSidebarMd) {
                setIsShowSidebarMd(true);
            }

            if (currentAccount && currentAccount.id) {
                try {
                    //fire both lookups in parallel — they hit separate endpoints
                    //but the result is only used together (both must equal 2 for
                    //the cards to render at all)
                    const [networkTypeResp, outputTypeResp] = await Promise.all([
                        apiGet('accounts/' + currentAccount.id + '/docconversionnetworktype', getAccessTokenSilently),
                        apiGet('accounts/' + currentAccount.id + '/docconversionoutputtype', getAccessTokenSilently)
                    ]);

                    if (networkTypeResp.ok) {
                        const networkType = await networkTypeResp.json();
                        setAccountDocConversionNetworkType(networkType || 0);
                    } else {
                        setAccountDocConversionNetworkType(0);
                    }

                    if (outputTypeResp.ok) {
                        const outputType = await outputTypeResp.json();
                        setAccountDocConversionOutputType(outputType || 0);
                    } else {
                        setAccountDocConversionOutputType(0);
                    }
                } catch (ex) {
                    console.log('Error fetching doc conversion settings: ' + ex.message);
                    setAccountDocConversionNetworkType(0);
                    setAccountDocConversionOutputType(0);
                }
            } else {
                setAccountDocConversionNetworkType(0);
                setAccountDocConversionOutputType(0);
            }
        })();
    }, []);

    // Single source of truth for whether the doc-conv cards should render at all.
    // Gate: the active account is configured as "TOTDATA - Cdex" (network=2 + output=2)
    // — i.e. its Document Conversion Revenue Format is CDEX, the same combination
    // AdminDashboard.js uses to identify CDEX-type doc conversion. Visible to any owner
    // whose account has that config (no admin/impersonate requirement). Anything else →
    // cards hidden.
    const showDocConversionCards =
        accountDocConversionNetworkType === 2
        && accountDocConversionOutputType === 2;

    const cardNavigate = (path) => {
        if (path === '/') {
            toggleIsShowSidebarMd();
        } else {
            navigate(path);
        }
    };

    //settingsCard accepts an optional `disabled` flag — when true the card is rendered
    //greyed out, no hover effects, no navigation. Used for permission-gated cards that
    //should still be visible (so the user knows the feature exists) but inaccessible.
    const settingsCard = (path, icon, label, description, barColor, disabled = false) => (
        <Card
            className="mb-3"
            style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderLeft: `3px solid ${disabled ? '#d1d5db' : barColor}`,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 10px rgba(0,0,0,.08)',
                transition: 'transform 150ms ease, box-shadow 150ms ease',
                opacity: disabled ? 0.55 : 1
            }}
            onClick={disabled ? undefined : () => cardNavigate(path)}
            onMouseEnter={disabled ? undefined : e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.14)'; }}
            onMouseLeave={disabled ? undefined : e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,.08)'; }}
            title={disabled ? 'Admin access required' : undefined}
        >
            <Card.Header style={{ background: 'transparent', border: 'none', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '9px', background: '#f3f4f6', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FontAwesomeIcon icon={icon} style={{ width: '16px', height: '16px', color: disabled ? '#9ca3af' : barColor }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '.06em', color: '#1f2937', lineHeight: '1.2' }}>{label}</div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '3px', fontWeight: '500', lineHeight: '1.3' }}>{description}</div>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} style={{ width: '13px', color: '#9ca3af', flexShrink: 0 }} />
                </div>
            </Card.Header>
        </Card>
    );

    const sectionLabel = (text) => (
        <p style={{ fontSize: '0.72rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '.08em', color: '#6b7280', marginBottom: '10px' }}>{text}</p>
    );

    const isOperator = accountProfile.accountType === "Admin" || accountProfile.accountType === "Sender" || accountProfile.accountType === "SenderReceiver";

    //If signed in as the singleton TOTData Global Mapping account, the override
    //cards instead point at the admin Global Mapping pages (same routes the admin
    //dashboard's Global Mappings tab uses) so the global account can edit its source
    //data from either entry point. All other accounts get the regular override pages.
    //
    //Primary check is IsGlobalMapping flag (Account.cs). accountName comparison is
    //a fallback while the flag is still being plumbed through the Auth0 claim —
    //once the flag arrives it short-circuits true first, so the name match becomes
    //a no-op and renaming the account (or shipping to Prod) won't break anything.
    const isGlobalAccount = accountProfile.isGlobalMapping
        || (accountProfile.accountName && accountProfile.accountName.toLowerCase() === 'totdata global mapping account');
    const cdexOverrideRoute = isGlobalAccount
        ? '/cdexcodes_dc/' + accountProfile.accountId
        : '/cdexcodesoverride_dc';
    const jibOverrideRoute = isGlobalAccount
        ? '/classificationcodes_dc/' + accountProfile.accountId
        : '/classificationcodesoverride_dc';

    const showCards = () => {
        if (isOperator) {
            return (
                <Container>
                    <Row className="justify-content-center">
                        <Col xs={12} md={5}>
                            {sectionLabel('Operated')}
                            {settingsCard('/mappedowners', faUsersGear, 'Owner Mapping', 'Configure and manage owner ID mappings', '#C8B568')}
                            {accountProfile.hasImpersonate && settingsCard('/cdexcodes', faLink, 'CDEX Account Mapping', 'Map CDEX account codes to your data', '#5B9BD5')}
                            {accountProfile.hasImpersonate && settingsCard('/classificationcodes', faSliders, 'JIB Classification Mapping', 'Map JIB classification codes', '#5B9BD5')}
                        </Col>
                        <Col xs={0} className="d-none d-md-block" style={{ flex: '0 0 2.08%', maxWidth: '2.08%' }} />
                        <Col xs={12} md={5}>
                            {sectionLabel('Non-Operated')}
                            {settingsCard('/cdexudfs', faCode, 'User-Defined Fields', 'Configure custom CDEX and XLS output fields', '#5B9BD5')}
                            {/* Doc-conversion-only cards: visible only when the active account
                                has doc conversion turned on AND its Document Conversion Revenue
                                Format is CDEX. Otherwise hidden entirely — see
                                showDocConversionCards above. */}
                            {showDocConversionCards && settingsCard(cdexOverrideRoute, faGear, 'Document Conversion CDEX Account Override', 'Override CDEX account-level settings', '#5B9BD5')}
                            {showDocConversionCards && settingsCard('/operatorcodes', faLink, 'Document Conversion CDEX Sender Codes', 'Map operator sender codes', '#5B9BD5')}
                            {/* JIB Classification Override card temporarily removed — restore by uncommenting:
                            {settingsCard(jibOverrideRoute, faSliders, 'JIB Classification Override', 'Override JIB classification mappings', '#5B9BD5')}
                            */}
                        </Col>
                    </Row>
                </Container>
            );
        }

        return (
            <Container>
                <Row className="justify-content-center">
                    <Col xs={12} sm={10} md={8} lg={6} xl={5}>
                        {settingsCard('/cdexudfs', faCode, 'User-Defined Fields', 'Configure custom CDEX and XLS output fields', '#5B9BD5')}
                        {/* Doc-conversion-only cards: see showDocConversionCards above —
                            only render when the account has doc conv + revenue output type CDEX. */}
                        {showDocConversionCards && settingsCard(cdexOverrideRoute, faGear, 'Document Conversion CDEX Account Override', 'Override CDEX account-level settings', '#5B9BD5')}
                        {showDocConversionCards && settingsCard('/operatorcodes', faLink, 'Document Conversion CDEX Sender Codes', 'Map operator sender codes', '#5B9BD5')}
                        {/* JIB Classification Override card temporarily removed — restore by uncommenting:
                        {settingsCard(jibOverrideRoute, faSliders, 'JIB Classification Override', 'Override JIB classification mappings', '#5B9BD5')}
                        */}
                    </Col>
                </Row>
            </Container>
        );
    };

    return (
        <>
            <br /><br />
            <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#333333', marginBottom: '4px', textAlign: 'center' }}>Settings</p>

            <div className="body flex-grow-1 px-3" style={{ marginTop: '0px' }}>
                <Container fluid="lg">
                    <Row style={{ height: '25px' }} />
                    <Row className="justify-content-center" style={{ marginBottom: '30px' }}>
                        <p style={{ fontSize: '1.15rem', fontWeight: '500', color: '#6b7280', margin: 0, textAlign: 'center' }}>What would you like to configure?</p>
                    </Row>
                    <Row style={{ marginTop: '20px' }}>
                        {showCards()}
                    </Row>
                </Container>
            </div>
        </>
    );
}

export default Settings;
